import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const ExchangeRates = () => {
  const { t } = useTranslation();
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [chartPeriod, setChartPeriod] = useState('week'); // day, week, month, year
  const [chartData, setChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);

  const fetchRates = async () => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('token');
      const response = await axios.get('/api/exchange-rates/current', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setRates(response.data.rates);
        setLastUpdate(new Date(response.data.timestamp));
      }
    } catch (err) {
      console.error('Failed to fetch exchange rates:', err);
      setError(t('exchangeRates.fetchError', 'Failed to fetch exchange rates'));
    } finally {
      setLoading(false);
    }
  };

  const getDateRange = (period) => {
    const endDate = new Date();
    const startDate = new Date();

    switch (period) {
      case 'day':
        // Last 24 hours (1 day)
        startDate.setDate(endDate.getDate() - 1);
        break;
      case 'week':
        // Last 7 days
        startDate.setDate(endDate.getDate() - 7);
        break;
      case 'month':
        // Last 30 days
        startDate.setMonth(endDate.getMonth() - 1);
        break;
      case 'year':
        // Last 365 days
        startDate.setFullYear(endDate.getFullYear() - 1);
        break;
      default:
        startDate.setDate(endDate.getDate() - 7);
    }

    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  };

  const fetchHistoricalData = async (period) => {
    try {
      setChartLoading(true);
      const token = localStorage.getItem('token');
      const { startDate, endDate } = getDateRange(period);

      // Fetch historical data for USD, EUR, CHF and gold using date ranges
      const [usdResponse, eurResponse, chfResponse, goldResponse] = await Promise.all([
        axios.get(`/api/exchange-rates/USD/history?startDate=${startDate}&endDate=${endDate}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`/api/exchange-rates/EUR/history?startDate=${startDate}&endDate=${endDate}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`/api/exchange-rates/CHF/history?startDate=${startDate}&endDate=${endDate}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`/api/exchange-rates/gold/history?startDate=${startDate}&endDate=${endDate}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (usdResponse.data.success && eurResponse.data.success && chfResponse.data.success && goldResponse.data.success) {
        // Merge data by date
        const usdData = usdResponse.data.data.rates;
        const eurData = eurResponse.data.data.rates;
        const chfData = chfResponse.data.data.rates;
        const goldData = goldResponse.data.data.rates;

        const mergedData = usdData.map((usdItem) => {
          const eurItem = eurData.find(e => e.date === usdItem.date);
          const chfItem = chfData.find(e => e.date === usdItem.date);
          const goldItem = goldData.find(e => e.date === usdItem.date);
          return {
            date: usdItem.date,
            USD: parseFloat(usdItem.rate),
            EUR: eurItem ? parseFloat(eurItem.rate) : null,
            CHF: chfItem ? parseFloat(chfItem.rate) : null,
            Gold: goldItem ? parseFloat(goldItem.rate) : null
          };
        });

        setChartData(mergedData);
      }
    } catch (err) {
      console.error('Failed to fetch historical data:', err);
    } finally {
      setChartLoading(false);
    }
  };

  useEffect(() => {
    fetchRates();

    // Auto-refresh every 30 minutes
    const interval = setInterval(() => {
      fetchRates();
    }, 30 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchHistoricalData(chartPeriod);
  }, [chartPeriod]);

  const formatRate = (rate) => {
    return parseFloat(rate).toFixed(4);
  };

  const formatTime = (date) => {
    if (!date) return '';
    return date.toLocaleTimeString('pl-PL', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCurrencyFlag = (currency) => {
    const flags = {
      'USD': '🇺🇸',
      'EUR': '🇪🇺',
      'CHF': '🇨🇭',
      'XAU': '🥇'
    };
    return flags[currency] || '💱';
  };

  if (loading && rates.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">
          {t('exchangeRates.title', 'Exchange Rates (PLN)')}
        </h3>
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pastel-purple-500"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">
          {t('exchangeRates.title', 'Exchange Rates (PLN)')}
        </h3>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-600 dark:text-red-400">{error}</p>
          <button
            onClick={fetchRates}
            className="mt-2 text-sm text-red-600 dark:text-red-400 underline hover:no-underline"
          >
            {t('exchangeRates.retry', 'Retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {t('exchangeRates.title', 'Exchange Rates (PLN)')}
        </h3>
        <button
          onClick={fetchRates}
          disabled={loading}
          className="text-sm text-pastel-purple-600 dark:text-pastel-purple-400 hover:text-pastel-purple-700 dark:hover:text-pastel-purple-300 disabled:opacity-50"
          title={t('exchangeRates.refresh', 'Refresh')}
        >
          <svg
            className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </button>
      </div>

      <div className="space-y-3">
        {rates.map((rateData) => (
          <div
            key={rateData.currency}
            className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
          >
            <div className="flex items-center space-x-3">
              <span className="text-2xl">{getCurrencyFlag(rateData.currency)}</span>
              <div>
                <p className="font-semibold text-gray-900 dark:text-gray-100">
                  {rateData.currency} / PLN
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {rateData.currencyName}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xl font-bold text-pastel-purple-600 dark:text-pastel-purple-400">
                {formatRate(rateData.rate)}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {rateData.effectiveDate}
              </p>
            </div>
          </div>
        ))}
      </div>

      {lastUpdate && (
        <div className="mt-4 text-xs text-gray-500 dark:text-gray-400 text-center">
          {t('exchangeRates.lastUpdate', 'Last updated')}: {formatTime(lastUpdate)}
        </div>
      )}

      {/* Historical Chart Section */}
      <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100">
            {t('exchangeRates.historicalTrends', 'Historical Trends')}
          </h4>

          {/* Period Selection */}
          <div className="flex gap-2">
            {['day', 'week', 'month', 'year'].map((period) => (
              <button
                key={period}
                onClick={() => setChartPeriod(period)}
                className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                  chartPeriod === period
                    ? 'bg-pastel-purple-600 text-white dark:bg-pastel-purple-500'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {t(`exchangeRates.${period}`, period.charAt(0).toUpperCase() + period.slice(1))}
              </button>
            ))}
          </div>
        </div>

        {/* Chart */}
        {chartLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pastel-purple-500"></div>
          </div>
        ) : chartData.length > 0 ? (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" className="dark:stroke-gray-700" />
                <XAxis
                  dataKey="date"
                  stroke="#6b7280"
                  className="dark:stroke-gray-400"
                  tick={{ fontSize: 12 }}
                />
                <YAxis
                  stroke="#6b7280"
                  className="dark:stroke-gray-400"
                  tick={{ fontSize: 12 }}
                  domain={['auto', 'auto']}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px'
                  }}
                  labelStyle={{ color: '#111827', fontWeight: 'bold' }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="USD"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={{ fill: '#3b82f6', r: 3 }}
                  activeDot={{ r: 5 }}
                  name="USD / PLN"
                />
                <Line
                  type="monotone"
                  dataKey="EUR"
                  stroke="#10b981"
                  strokeWidth={2}
                  dot={{ fill: '#10b981', r: 3 }}
                  activeDot={{ r: 5 }}
                  name="EUR / PLN"
                />
                <Line
                  type="monotone"
                  dataKey="CHF"
                  stroke="#ef4444"
                  strokeWidth={2}
                  dot={{ fill: '#ef4444', r: 3 }}
                  activeDot={{ r: 5 }}
                  name="CHF / PLN"
                />
                <Line
                  type="monotone"
                  dataKey="Gold"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  dot={{ fill: '#f59e0b', r: 3 }}
                  activeDot={{ r: 5 }}
                  name="Gold (1g)"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            {t('exchangeRates.noChartData', 'No historical data available')}
          </div>
        )}
      </div>

      <div className="mt-4 text-xs text-gray-400 dark:text-gray-500 text-center">
        {t('exchangeRates.source', 'Source: National Bank of Poland (NBP)')}
      </div>
    </div>
  );
};

export default ExchangeRates;
